import React, { useEffect, useState, useMemo } from "react";

const API_BASE = "https://api.visiontrackcrm.com";

const resolveImageUrl = (url) => {
  if (!url || typeof url !== "string") return url;
  if (url.startsWith("data:") || url.startsWith("blob:") || url.startsWith("file:")) return url;
  if (url.startsWith("/")) return `${API_BASE}${url}`;
  if (url.includes("/images/")) {
    const path = url.substring(url.indexOf("/images/"));
    return `${API_BASE}${path}`;
  }
  return url;
};

const ProductApproval = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Store History Modal State
  const [storeHistoryModal, setStoreHistoryModal] = useState({
    isOpen: false,
    storeCode: "",
    storeName: "",
    ownerName: "",
    mobile: "",
    email: "",
    products: [],
    loading: false,
    historyFilter: "all",
  });

  // =====================================================
  // FETCH ALL PRODUCTS FOR SUPER ADMIN
  // =====================================================
  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/products/adminall`);
      const data = await response.json();

      if (data?.success && Array.isArray(data.data)) {
        setProducts(data.data);
      } else {
        setProducts([]);
      }
    } catch (error) {
      console.error("FETCH PRODUCTS ERROR:", error);
      alert("Failed to load products from server");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // =====================================================
  // APPROVE / REJECT PRODUCT
  // =====================================================
  const handleApproval = async (productId, status) => {
    const action = status === "approved" ? "approve" : "reject";
    let rejectionReason = "";

    if (status === "rejected") {
      rejectionReason = window.prompt(
        "Enter reason for rejecting this product (optional):",
        "Product details do not meet marketplace standards"
      );
      if (rejectionReason === null) return; // User cancelled prompt
    } else {
      const confirmed = window.confirm(
        `Are you sure you want to approve this product for wholesale listing?`
      );
      if (!confirmed) return;
    }

    try {
      setActionLoading(productId);

      const response = await fetch(
        `${API_BASE}/products/approval/${productId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            status,
            rejection_reason: rejectionReason,
          }),
        }
      );

      const data = await response.json();

      if (data?.success) {
        alert(
          status === "approved"
            ? "✅ Product approved successfully and published to wholesale marketplace."
            : "❌ Product rejected successfully."
        );

        setSelectedProduct(null);
        await fetchProducts();

        // If history modal is open, refresh store history too
        if (storeHistoryModal.isOpen && storeHistoryModal.storeCode) {
          openStoreHistory(
            storeHistoryModal.storeCode,
            storeHistoryModal.storeName,
            storeHistoryModal.ownerName,
            storeHistoryModal.mobile,
            storeHistoryModal.email
          );
        }
      } else {
        alert(data?.message || `Failed to ${action} product`);
      }
    } catch (error) {
      console.error("APPROVAL ERROR:", error);
      alert(`Unable to ${action} product`);
    } finally {
      setActionLoading(null);
    }
  };

  // =====================================================
  // DELETE PRODUCT (SUPER ADMIN)
  // =====================================================
  const handleDeleteProduct = async (productId, productName = "this product") => {
    const confirmed = window.confirm(
      `Are you sure you want to permanently delete "${productName}" from the catalog?`
    );
    if (!confirmed) return;

    try {
      setActionLoading(productId);
      const response = await fetch(`${API_BASE}/products/${productId}`, {
        method: "DELETE",
      });
      const data = await response.json();

      if (data?.success) {
        alert("🗑️ Product deleted successfully.");
        if (selectedProduct?.id === productId) setSelectedProduct(null);
        await fetchProducts();

        if (storeHistoryModal.isOpen && storeHistoryModal.storeCode) {
          openStoreHistory(
            storeHistoryModal.storeCode,
            storeHistoryModal.storeName,
            storeHistoryModal.ownerName,
            storeHistoryModal.mobile,
            storeHistoryModal.email
          );
        }
      } else {
        alert(data?.message || "Failed to delete product");
      }
    } catch (error) {
      console.error("DELETE PRODUCT ERROR:", error);
      alert("Unable to delete product: " + error.message);
    } finally {
      setActionLoading(null);
    }
  };

  // =====================================================
  // SUSPEND PRODUCT (HIDES FROM RETAIL STORE)
  // =====================================================
  const handleSuspendProduct = async (productId, productName = "this product") => {
    const reason = window.prompt(
      `Enter reason for suspending "${productName}" (optional):\n\nSuspended products will immediately be hidden from retail stores.`,
      "Suspended by Super Admin"
    );
    if (reason === null) return;

    try {
      setActionLoading(productId);
      const response = await fetch(`${API_BASE}/products/suspend/${productId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason }),
      });
      const data = await response.json();

      if (data?.success) {
        alert(`⏸️ "${productName}" suspended. It is now hidden from retail stores.`);
        if (selectedProduct?.id === productId) {
          setSelectedProduct((prev) => prev ? { ...prev, approval_status: "suspended", rejection_reason: reason } : null);
        }
        await fetchProducts();

        if (storeHistoryModal.isOpen && storeHistoryModal.storeCode) {
          openStoreHistory(
            storeHistoryModal.storeCode,
            storeHistoryModal.storeName,
            storeHistoryModal.ownerName,
            storeHistoryModal.mobile,
            storeHistoryModal.email
          );
        }
      } else {
        alert(data?.message || "Failed to suspend product");
      }
    } catch (error) {
      console.error("SUSPEND ERROR:", error);
      alert("Unable to suspend product: " + error.message);
    } finally {
      setActionLoading(null);
    }
  };

  // =====================================================
  // REVOKE SUSPENSION / UNSUSPEND PRODUCT (RESTORES LIVE)
  // =====================================================
  const handleUnsuspendProduct = async (productId, productName = "this product") => {
    const confirmed = window.confirm(
      `Revoke suspension for "${productName}"?\n\nThe product will become active and visible again in retail stores.`
    );
    if (!confirmed) return;

    try {
      setActionLoading(productId);
      const response = await fetch(`${API_BASE}/products/unsuspend/${productId}`, {
        method: "PUT",
      });
      const data = await response.json();

      if (data?.success) {
        alert(`✅ Suspension revoked for "${productName}". The product is now live in retail stores.`);
        if (selectedProduct?.id === productId) {
          setSelectedProduct((prev) => prev ? { ...prev, approval_status: "approved", rejection_reason: null } : null);
        }
        await fetchProducts();

        if (storeHistoryModal.isOpen && storeHistoryModal.storeCode) {
          openStoreHistory(
            storeHistoryModal.storeCode,
            storeHistoryModal.storeName,
            storeHistoryModal.ownerName,
            storeHistoryModal.mobile,
            storeHistoryModal.email
          );
        }
      } else {
        alert(data?.message || "Failed to revoke suspension");
      }
    } catch (error) {
      console.error("REVOKE SUSPENSION ERROR:", error);
      alert("Unable to revoke suspension: " + error.message);
    } finally {
      setActionLoading(null);
    }
  };

  // =====================================================
  // VIEW PREVIOUS PRODUCTS OF A STORE
  // =====================================================
  const openStoreHistory = async (storeCode, storeName, ownerName, mobile, email) => {
    const code = storeCode || "STORE001";
    setStoreHistoryModal({
      isOpen: true,
      storeCode: code,
      storeName: storeName || code,
      ownerName: ownerName || "Store Owner",
      mobile: mobile || "",
      email: email || "",
      products: [],
      loading: true,
      historyFilter: "all",
    });

    try {
      // Fetch products by store code from backend
      const res = await fetch(`${API_BASE}/products/store/${encodeURIComponent(code)}`);
      const data = await res.json();

      let storeProducts = [];
      if (data?.success && Array.isArray(data.data) && data.data.length > 0) {
        storeProducts = data.data;
      } else {
        // Filter from current products array as fallback
        storeProducts = products.filter(
          (p) =>
            (p.store_code && String(p.store_code).toLowerCase() === String(code).toLowerCase()) ||
            (p.applicant_store_code && String(p.applicant_store_code).toLowerCase() === String(code).toLowerCase()) ||
            (p.store_name && String(p.store_name).toLowerCase() === String(storeName).toLowerCase())
        );
      }

      setStoreHistoryModal((prev) => ({
        ...prev,
        products: storeProducts,
        loading: false,
      }));
    } catch (err) {
      console.error("Store history fetch error:", err);
      // Fallback from local list
      const storeProducts = products.filter(
        (p) =>
          (p.store_code && String(p.store_code).toLowerCase() === String(code).toLowerCase()) ||
          (p.applicant_store_code && String(p.applicant_store_code).toLowerCase() === String(code).toLowerCase())
      );
      setStoreHistoryModal((prev) => ({
        ...prev,
        products: storeProducts,
        loading: false,
      }));
    }
  };

  // =====================================================
  // FILTER & SEARCH LOGIC
  // =====================================================
  const getStatus = (product) => {
    return (
      product.approval_status ||
      product.status ||
      "pending"
    ).toLowerCase();
  };

  const getStatusClass = (status) => {
    if (status === "approved") return "status-approved";
    if (status === "suspended") return "status-suspended";
    if (status === "rejected") return "status-rejected";
    return "status-pending";
  };

  const getVariants = (product) => {
    if (!product.variants) return [];
    if (Array.isArray(product.variants)) return product.variants;
    try {
      return JSON.parse(product.variants);
    } catch {
      return [];
    }
  };

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const status = getStatus(product);
      if (filter !== "all" && status !== filter) {
        return false;
      }

      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const name = (product.product_name || "").toLowerCase();
        const brand = (product.brand || "").toLowerCase();
        const cat = (product.category || "").toLowerCase();
        const store = (product.store_name || "").toLowerCase();
        const code = (product.applicant_store_code || product.store_code || "").toLowerCase();
        const owner = (product.owner_name || "").toLowerCase();

        return (
          name.includes(query) ||
          brand.includes(query) ||
          cat.includes(query) ||
          store.includes(query) ||
          code.includes(query) ||
          owner.includes(query)
        );
      }

      return true;
    });
  }, [products, filter, searchQuery]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="approval-page">
      {/* ============================================= */}
      {/* PAGE HEADER */}
      {/* ============================================= */}
      <div className="page-header">
        <div>
          <h1>Product Approval & Store Catalog Review</h1>
          <p>
            Review and approve wholesale products submitted by verified optical stores & sellers before publishing to marketplace.
          </p>
        </div>

        <div className="header-actions">
          <button
            className="refresh-btn"
            onClick={fetchProducts}
            disabled={loading}
          >
            ↻ Refresh Catalog
          </button>
        </div>
      </div>

      {/* ============================================= */}
      {/* SEARCH AND FILTER BAR */}
      {/* ============================================= */}
      <div className="controls-bar">
        <div className="filter-container">
          <button
            className={filter === "all" ? "filter-btn active" : "filter-btn"}
            onClick={() => setFilter("all")}
          >
            All Submissions
            <span className="badge-count">{products.length}</span>
          </button>

          <button
            className={filter === "approved" ? "filter-btn active" : "filter-btn"}
            onClick={() => setFilter("approved")}
          >
            Approved & Live
            <span className="badge-count">
              {products.filter((p) => getStatus(p) === "approved").length}
            </span>
          </button>

          <button
            className={filter === "suspended" ? "filter-btn active" : "filter-btn"}
            onClick={() => setFilter("suspended")}
          >
            Suspended
            <span className="badge-count" style={{ background: filter === "suspended" ? "rgba(255,255,255,0.3)" : "#fef3c7", color: filter === "suspended" ? "#fff" : "#b45309" }}>
              {products.filter((p) => getStatus(p) === "suspended").length}
            </span>
          </button>

          <button
            className={filter === "pending" ? "filter-btn active" : "filter-btn"}
            onClick={() => setFilter("pending")}
          >
            Pending Review
            <span className="badge-count">
              {products.filter((p) => getStatus(p) === "pending").length}
            </span>
          </button>

          <button
            className={filter === "rejected" ? "filter-btn active" : "filter-btn"}
            onClick={() => setFilter("rejected")}
          >
            Rejected
            <span className="badge-count">
              {products.filter((p) => getStatus(p) === "rejected").length}
            </span>
          </button>
        </div>

        <div className="search-wrap">
          <input
            type="text"
            className="search-input"
            placeholder="Search by Product, Brand, Category, Store Name or Code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="clear-search-btn" onClick={() => setSearchQuery("")}>
              ✕
            </button>
          )}
        </div>
      </div>

      {/* ============================================= */}
      {/* PRODUCT LIST GRID */}
      {/* ============================================= */}
      {loading ? (
        <div className="loading-box">
          <div className="spinner"></div>
          <p>Fetching product submissions and store details...</p>
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="empty-box">
          <div className="empty-icon">📦</div>
          <h2>No products found</h2>
          <p>
            {searchQuery
              ? `No products matching "${searchQuery}" in ${filter} filter.`
              : `There are currently no ${filter} product submissions.`}
          </p>
        </div>
      ) : (
        <div className="product-grid">
          {filteredProducts.map((product) => {
            const status = getStatus(product);
            const variants = getVariants(product);
            const firstImage =
              variants?.[0]?.image ||
              product.image_url ||
              product.image;

            const storeCode =
              product.applicant_store_code ||
              product.store_code ||
              "STORE001";
            const storeName =
              product.store_name ||
              product.seller_name ||
              storeCode;
            const ownerName = product.owner_name || "Store Owner";
            const storeContact = product.store_mobile || product.seller_phone || product.store_email || "";

            return (
              <div className="product-card" key={product.id}>
                {/* APPLICANT STORE BANNER */}
                <div className="applicant-banner">
                  <div className="store-badge-left">
                    <span className="store-icon">🏪</span>
                    <div className="store-meta">
                      <strong className="store-title" title={storeName}>
                        {storeName}
                      </strong>
                      <span className="store-code-pill">{storeCode}</span>
                    </div>
                  </div>

                  <button
                    className="history-pill-btn"
                    title={`View previous products submitted by ${storeName}`}
                    onClick={() =>
                      openStoreHistory(
                        storeCode,
                        storeName,
                        ownerName,
                        product.store_mobile || product.seller_phone,
                        product.store_email || product.seller_email
                      )
                    }
                  >
                    📜 Previous Products
                  </button>
                </div>

                {/* STORE APPLICANT OWNER & SUBMISSION DATE */}
                <div className="applicant-subinfo">
                  <span>👤 {ownerName} {storeContact ? `• 📞 ${storeContact}` : ""}</span>
                  <span>📅 {formatDate(product.created_at)}</span>
                </div>

                {/* IMAGE & STATUS BADGE */}
                <div className="product-image">
                  {firstImage ? (
                    <img src={firstImage} alt={product.product_name} />
                  ) : (
                    <div className="no-image">👓</div>
                  )}

                  <span className={`status-badge ${getStatusClass(status)}`}>
                    {status === "pending" && "⏳ "}
                    {status === "approved" && "✅ "}
                    {status === "suspended" && "⏸️ "}
                    {status === "rejected" && "❌ "}
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </span>
                </div>

                {/* INFO */}
                <div className="product-info">
                  <div className="product-category-row">
                    <span className="product-category">
                      {product.category || "Opticals"}
                    </span>
                    <span className="product-brand-tag">
                      {product.brand || "VisionTrack"}
                    </span>
                  </div>

                  <h3 className="product-title" title={product.product_name}>
                    {product.product_name}
                  </h3>

                  <p className="description">
                    {product.description || "No description provided."}
                  </p>

                  {/* VARIANTS PREVIEW */}
                  {variants.length > 0 && (
                    <div className="variants">
                      <div className="variant-header-row">
                        <strong>Variants ({variants.length})</strong>
                        <span className="price-tag">
                          ₹{variants[0]?.price || product.price || "0"}
                        </span>
                      </div>

                      <div className="variant-list">
                        {variants.map((variant, index) => (
                          <div className="variant" key={index}>
                            {variant.image ? (
                              <img src={resolveImageUrl(variant.image)} alt={variant.color} />
                            ) : (
                              <div className="variant-placeholder">👓</div>
                            )}
                            <div className="variant-info">
                              <span className="variant-color">{variant.color || "Default"}</span>
                              <strong className="variant-price">₹{variant.price || "0"}</strong>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ACTION BUTTONS */}
                  <div className="action-row">
                    <button
                      className="view-btn"
                      onClick={() => setSelectedProduct(product)}
                    >
                      🔍 Full Details
                    </button>

                    {status === "pending" && (
                      <>
                        <button
                          className="reject-btn"
                          disabled={actionLoading === product.id}
                          onClick={() => handleApproval(product.id, "rejected")}
                        >
                          {actionLoading === product.id ? "..." : "Reject"}
                        </button>

                        <button
                          className="approve-btn"
                          disabled={actionLoading === product.id}
                          onClick={() => handleApproval(product.id, "approved")}
                        >
                          {actionLoading === product.id ? "..." : "Approve"}
                        </button>
                      </>
                    )}

                    {status === "approved" && (
                      <button
                        className="suspend-btn"
                        disabled={actionLoading === product.id}
                        onClick={() => handleSuspendProduct(product.id, product.product_name)}
                        title="Suspend product from retail stores"
                      >
                        {actionLoading === product.id ? "..." : "⏸️ Suspend"}
                      </button>
                    )}

                    {status === "suspended" && (
                      <button
                        className="unsuspend-btn"
                        disabled={actionLoading === product.id}
                        onClick={() => handleUnsuspendProduct(product.id, product.product_name)}
                        title="Revoke suspension and restore to retail stores"
                      >
                        {actionLoading === product.id ? "..." : "▶️ Activate"}
                      </button>
                    )}

                    {status === "rejected" && (
                      <button
                        className="approve-btn"
                        disabled={actionLoading === product.id}
                        onClick={() => handleApproval(product.id, "approved")}
                        title="Approve / Restore product"
                      >
                        {actionLoading === product.id ? "..." : "Approve"}
                      </button>
                    )}

                    <button
                      className="history-view-btn"
                      onClick={() =>
                        openStoreHistory(
                          storeCode,
                          storeName,
                          ownerName,
                          product.store_mobile || product.seller_phone,
                          product.store_email || product.seller_email
                        )
                      }
                      title="View all products by this store"
                    >
                      History
                    </button>

                    <button
                      className="delete-btn"
                      disabled={actionLoading === product.id}
                      onClick={() => handleDeleteProduct(product.id, product.product_name)}
                      title="Permanently delete product from DB"
                    >
                      {actionLoading === product.id ? "..." : "🗑️"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ============================================= */}
      {/* 1. PRODUCT DETAIL MODAL */}
      {/* ============================================= */}
      {selectedProduct && (
        <div className="modal-overlay" onClick={() => setSelectedProduct(null)}>
          <div className="product-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>{selectedProduct.product_name}</h2>
                <p>Submitted Product Details & Application Info</p>
              </div>
              <button className="close-btn" onClick={() => setSelectedProduct(null)}>
                ×
              </button>
            </div>

            <div className="modal-content">
              {/* STORE APPLICANT SUMMARY BOX */}
              <div className="store-applicant-card">
                <div className="store-card-header">
                  <div>
                    <span className="applicant-badge">APPLICANT STORE DETAILS</span>
                    <h3>
                      {selectedProduct.store_name ||
                        selectedProduct.seller_name ||
                        selectedProduct.applicant_store_code ||
                        selectedProduct.store_code ||
                        "Store Account"}
                    </h3>
                  </div>
                  <span className="code-pill">
                    {selectedProduct.applicant_store_code || selectedProduct.store_code || "STORE001"}
                  </span>
                </div>

                <div className="applicant-grid">
                  <div className="applicant-field">
                    <span>Authorized Owner:</span>
                    <strong>{selectedProduct.owner_name || "Store Owner"}</strong>
                  </div>
                  <div className="applicant-field">
                    <span>Contact Number:</span>
                    <strong>{selectedProduct.store_mobile || selectedProduct.seller_phone || "Not provided"}</strong>
                  </div>
                  <div className="applicant-field">
                    <span>Store Email:</span>
                    <strong>{selectedProduct.store_email || selectedProduct.seller_email || "Not provided"}</strong>
                  </div>
                  <div className="applicant-field">
                    <span>Submission Date:</span>
                    <strong>{formatDate(selectedProduct.created_at)}</strong>
                  </div>
                </div>

                <button
                  className="store-history-cta"
                  onClick={() => {
                    const code = selectedProduct.applicant_store_code || selectedProduct.store_code;
                    const name = selectedProduct.store_name || selectedProduct.seller_name || code;
                    openStoreHistory(
                      code,
                      name,
                      selectedProduct.owner_name,
                      selectedProduct.store_mobile || selectedProduct.seller_phone,
                      selectedProduct.store_email || selectedProduct.seller_email
                    );
                  }}
                >
                  📜 View All Previous Products From This Store
                </button>
              </div>

              {/* PRODUCT SPECIFICS */}
              <div className="detail-row">
                <span>Category</span>
                <strong>{selectedProduct.category || "Opticals"}</strong>
              </div>

              <div className="detail-row">
                <span>Brand</span>
                <strong>{selectedProduct.brand || "VisionTrack"}</strong>
              </div>

              <div className="detail-row">
                <span>Approval Status</span>
                <strong className={getStatusClass(getStatus(selectedProduct))}>
                  {getStatus(selectedProduct).toUpperCase()}
                </strong>
              </div>

              {selectedProduct.mrp && (
                <div className="detail-row">
                  <span>Standard MRP</span>
                  <strong>₹{Number(selectedProduct.mrp).toLocaleString()}</strong>
                </div>
              )}

              {selectedProduct.wholesale_price && (
                <div className="detail-row">
                  <span>Base Wholesale Price</span>
                  <strong style={{ color: "#2563EB" }}>₹{Number(selectedProduct.wholesale_price).toLocaleString()}</strong>
                </div>
              )}

              {selectedProduct.rejection_reason && (
                <div className="rejection-reason-box">
                  <strong>Rejection Note:</strong>
                  <p>{selectedProduct.rejection_reason}</p>
                </div>
              )}

              {/* SPECIFICATIONS & OPTICAL TAGS */}
              {Array.isArray(selectedProduct.specifications) && selectedProduct.specifications.length > 0 && (
                <div style={{ marginTop: 12, marginBottom: 12 }}>
                  <h4 style={{ fontSize: 13, fontWeight: "700", marginBottom: 6 }}>Product Specifications & Tags</h4>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {selectedProduct.specifications.map((spec, sIdx) => (
                      <span
                        key={sIdx}
                        style={{
                          backgroundColor: "#F1F5F9",
                          border: "1px solid #CBD5E1",
                          borderRadius: 6,
                          padding: "4px 8px",
                          fontSize: 12,
                          color: "#334155",
                        }}
                      >
                        <strong>{spec.tag}:</strong> {spec.detail}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* VOLUME DISCOUNT TIER SLABS */}
              {Array.isArray(selectedProduct.tierOfferings) && selectedProduct.tierOfferings.length > 0 && (
                <div style={{ marginTop: 14, marginBottom: 14 }}>
                  <h4 style={{ fontSize: 13, fontWeight: "700", marginBottom: 6 }}>
                    Tier Volume Pricing ("Buy More, Pay Less")
                  </h4>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, border: "1px solid #E2E8F0" }}>
                    <thead>
                      <tr style={{ backgroundColor: "#F8FAFC", textAlign: "left" }}>
                        <th style={{ padding: "6px 10px", borderBottom: "1px solid #E2E8F0" }}>Quantity Slab</th>
                        <th style={{ padding: "6px 10px", borderBottom: "1px solid #E2E8F0" }}>Rate / Unit</th>
                        <th style={{ padding: "6px 10px", borderBottom: "1px solid #E2E8F0" }}>Offer Benefit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedProduct.tierOfferings.map((tier, tIdx) => (
                        <tr key={tIdx} style={{ borderBottom: "1px solid #F1F5F9" }}>
                          <td style={{ padding: "6px 10px" }}>
                            {tier.minQty}{tier.maxQty ? ` - ${tier.maxQty}` : "+"} pcs
                          </td>
                          <td style={{ padding: "6px 10px", fontWeight: "700", color: "#2563EB" }}>
                            ₹{Number(tier.unitPrice).toLocaleString()}
                          </td>
                          <td style={{ padding: "6px 10px", color: "#16A34A", fontWeight: "600" }}>
                            {tier.discount || "Bulk Slab"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="description-box">
                <h4>Product Description</h4>
                <p>{selectedProduct.description || "No description provided."}</p>
              </div>

              {/* VARIANTS */}
              <h4>Product Variants & Pricing</h4>
              <div className="modal-variants">
                {getVariants(selectedProduct).map((variant, index) => (
                  <div className="modal-variant" key={index}>
                    {variant.image ? (
                      <img src={resolveImageUrl(variant.image)} alt={variant.color} />
                    ) : (
                      <div className="modal-variant-placeholder">👓</div>
                    )}
                    <div>
                      <strong>Color: {variant.color || "Default"}</strong>
                      <p>Wholesale Rate: ₹{variant.price || "0"}</p>
                      {variant.sku && <p>SKU: {variant.sku}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* MODAL ACTIONS */}
            <div className="modal-actions">
              <button
                className="modal-delete-btn"
                disabled={actionLoading === selectedProduct.id}
                onClick={() => handleDeleteProduct(selectedProduct.id, selectedProduct.product_name)}
                title="Permanently remove from database"
              >
                🗑️ Delete Product
              </button>

              {getStatus(selectedProduct) === "approved" && (
                <button
                  className="modal-suspend-btn"
                  disabled={actionLoading === selectedProduct.id}
                  onClick={() => handleSuspendProduct(selectedProduct.id, selectedProduct.product_name)}
                >
                  ⏸️ Suspend Product
                </button>
              )}

              {getStatus(selectedProduct) === "suspended" && (
                <button
                  className="modal-unsuspend-btn"
                  disabled={actionLoading === selectedProduct.id}
                  onClick={() => handleUnsuspendProduct(selectedProduct.id, selectedProduct.product_name)}
                >
                  ▶️ Revoke Suspension / Go Live
                </button>
              )}

              {getStatus(selectedProduct) === "pending" && (
                <>
                  <button
                    className="reject-btn"
                    disabled={actionLoading === selectedProduct.id}
                    onClick={() => handleApproval(selectedProduct.id, "rejected")}
                  >
                    Reject Product
                  </button>
                  <button
                    className="approve-btn"
                    disabled={actionLoading === selectedProduct.id}
                    onClick={() => handleApproval(selectedProduct.id, "approved")}
                  >
                    Approve Product
                  </button>
                </>
              )}

              {getStatus(selectedProduct) === "rejected" && (
                <button
                  className="approve-btn"
                  disabled={actionLoading === selectedProduct.id}
                  onClick={() => handleApproval(selectedProduct.id, "approved")}
                >
                  Approve / Restore Product
                </button>
              )}

              <button
                className="close-modal-btn"
                onClick={() => setSelectedProduct(null)}
              >
                Close Window
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================= */}
      {/* 2. STORE PREVIOUS PRODUCTS HISTORY MODAL */}
      {/* ============================================= */}
      {storeHistoryModal.isOpen && (
        <div
          className="modal-overlay"
          onClick={() => setStoreHistoryModal((prev) => ({ ...prev, isOpen: false }))}
        >
          <div className="store-history-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header history-modal-header">
              <div>
                <h2>Store Catalog History: {storeHistoryModal.storeName}</h2>
                <p>
                  Store Code: <strong>{storeHistoryModal.storeCode}</strong> • Owner: {storeHistoryModal.ownerName}
                  {storeHistoryModal.mobile ? ` • Phone: ${storeHistoryModal.mobile}` : ""}
                </p>
              </div>
              <button
                className="close-btn"
                onClick={() => setStoreHistoryModal((prev) => ({ ...prev, isOpen: false }))}
              >
                ×
              </button>
            </div>

            <div className="history-modal-content">
              {/* STORE STATS BAR */}
              <div className="history-stats-bar">
                <div className="stat-card">
                  <span className="stat-label">Total Submissions</span>
                  <strong className="stat-value">{storeHistoryModal.products.length}</strong>
                </div>
                <div className="stat-card approved-stat">
                  <span className="stat-label">Approved & Active</span>
                  <strong className="stat-value">
                    {storeHistoryModal.products.filter((p) => getStatus(p) === "approved").length}
                  </strong>
                </div>
                <div className="stat-card pending-stat">
                  <span className="stat-label">Pending Review</span>
                  <strong className="stat-value">
                    {storeHistoryModal.products.filter((p) => getStatus(p) === "pending").length}
                  </strong>
                </div>
                <div className="stat-card rejected-stat">
                  <span className="stat-label">Rejected</span>
                  <strong className="stat-value">
                    {storeHistoryModal.products.filter((p) => getStatus(p) === "rejected").length}
                  </strong>
                </div>
              </div>

              {/* FILTER TABS INSIDE HISTORY */}
              <div className="history-filter-row">
                <button
                  className={storeHistoryModal.historyFilter === "all" ? "sub-filter active" : "sub-filter"}
                  onClick={() => setStoreHistoryModal((p) => ({ ...p, historyFilter: "all" }))}
                >
                  All ({storeHistoryModal.products.length})
                </button>
                <button
                  className={storeHistoryModal.historyFilter === "approved" ? "sub-filter active" : "sub-filter"}
                  onClick={() => setStoreHistoryModal((p) => ({ ...p, historyFilter: "approved" }))}
                >
                  Approved ({storeHistoryModal.products.filter((p) => getStatus(p) === "approved").length})
                </button>
                <button
                  className={storeHistoryModal.historyFilter === "suspended" ? "sub-filter active" : "sub-filter"}
                  onClick={() => setStoreHistoryModal((p) => ({ ...p, historyFilter: "suspended" }))}
                >
                  Suspended ({storeHistoryModal.products.filter((p) => getStatus(p) === "suspended").length})
                </button>
                <button
                  className={storeHistoryModal.historyFilter === "pending" ? "sub-filter active" : "sub-filter"}
                  onClick={() => setStoreHistoryModal((p) => ({ ...p, historyFilter: "pending" }))}
                >
                  Pending ({storeHistoryModal.products.filter((p) => getStatus(p) === "pending").length})
                </button>
                <button
                  className={storeHistoryModal.historyFilter === "rejected" ? "sub-filter active" : "sub-filter"}
                  onClick={() => setStoreHistoryModal((p) => ({ ...p, historyFilter: "rejected" }))}
                >
                  Rejected ({storeHistoryModal.products.filter((p) => getStatus(p) === "rejected").length})
                </button>
              </div>

              {/* STORE PRODUCTS LIST */}
              {storeHistoryModal.loading ? (
                <div className="loading-box">
                  <div className="spinner"></div>
                  <p>Loading previous store submissions...</p>
                </div>
              ) : storeHistoryModal.products.length === 0 ? (
                <div className="empty-box">
                  <div className="empty-icon">🏪</div>
                  <h3>No previous products found for this store.</h3>
                </div>
              ) : (
                <div className="history-product-grid">
                  {storeHistoryModal.products
                    .filter((p) => {
                      if (storeHistoryModal.historyFilter === "all") return true;
                      return getStatus(p) === storeHistoryModal.historyFilter;
                    })
                    .map((item) => {
                      const itemStatus = getStatus(item);
                      const itemVariants = getVariants(item);
                      const firstImg = itemVariants?.[0]?.image || item.image_url;

                      return (
                        <div className="history-product-card" key={item.id}>
                          <div className="history-card-img">
                            {firstImg ? (
                              <img src={firstImg} alt={item.product_name} />
                            ) : (
                              <span>👓</span>
                            )}
                            <span className={`status-badge ${getStatusClass(itemStatus)}`}>
                              {itemStatus.toUpperCase()}
                            </span>
                          </div>

                          <div className="history-card-body">
                            <span className="history-category">{item.category || "Opticals"}</span>
                            <h4>{item.product_name}</h4>
                            <p className="history-brand">Brand: <strong>{item.brand || "VisionTrack"}</strong></p>
                            <p className="history-price">
                              Price: <strong>₹{itemVariants?.[0]?.price || item.price || "0"}</strong>
                            </p>
                            <span className="history-date">📅 {formatDate(item.created_at)}</span>

                            <div className="history-action-btns">
                              {itemStatus === "pending" && (
                                <>
                                  <button
                                    className="mini-reject"
                                    onClick={() => handleApproval(item.id, "rejected")}
                                  >
                                    Reject
                                  </button>
                                  <button
                                    className="mini-approve"
                                    onClick={() => handleApproval(item.id, "approved")}
                                  >
                                    Approve
                                  </button>
                                </>
                              )}
                              {itemStatus === "approved" && (
                                <button
                                  className="mini-reject"
                                  onClick={() => handleSuspendProduct(item.id, item.product_name)}
                                  title="Suspend product from retail stores"
                                >
                                  Suspend
                                </button>
                              )}
                              {itemStatus === "suspended" && (
                                <button
                                  className="mini-approve"
                                  onClick={() => handleUnsuspendProduct(item.id, item.product_name)}
                                  title="Revoke suspension"
                                >
                                  Activate
                                </button>
                              )}
                              <button
                                className="mini-delete"
                                onClick={() => handleDeleteProduct(item.id, item.product_name)}
                                title="Delete Product"
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button
                className="close-modal-btn"
                onClick={() => setStoreHistoryModal((prev) => ({ ...prev, isOpen: false }))}
              >
                Close Store History
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================= */}
      {/* CSS STYLES */}
      {/* ============================================= */}
      <style>{`
        * {
          box-sizing: border-box;
        }

        .approval-page {
          min-height: 100vh;
          background: #f4f7fb;
          padding: 24px;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        /* HEADER */
        .page-header {
          background: #ffffff;
          border-radius: 16px;
          padding: 22px 28px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 20px;
          margin-bottom: 20px;
          box-shadow: 0 4px 18px rgba(0, 0, 0, 0.04);
          border: 1px solid #e2e8f0;
        }

        .page-header h1 {
          margin: 0;
          color: #0f172a;
          font-size: 24px;
          font-weight: 800;
        }

        .page-header p {
          margin: 6px 0 0;
          color: #64748b;
          font-size: 13.5px;
        }

        .refresh-btn {
          border: none;
          background: #2563eb;
          color: white;
          padding: 11px 20px;
          border-radius: 10px;
          cursor: pointer;
          font-weight: 700;
          font-size: 13.5px;
          box-shadow: 0 2px 8px rgba(37, 99, 235, 0.25);
          transition: all 0.2s ease;
        }

        .refresh-btn:hover {
          background: #1d4ed8;
        }

        /* CONTROLS BAR */
        .controls-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
          margin-bottom: 22px;
        }

        .filter-container {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .filter-btn {
          border: 1px solid #cbd5e1;
          background: #ffffff;
          color: #475569;
          padding: 9px 15px;
          border-radius: 10px;
          cursor: pointer;
          font-weight: 700;
          font-size: 13px;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.15s ease;
        }

        .filter-btn .badge-count {
          background: #f1f5f9;
          color: #334155;
          padding: 2px 8px;
          border-radius: 20px;
          font-size: 11.5px;
        }

        .filter-btn.active {
          background: #2563eb;
          color: #ffffff;
          border-color: #2563eb;
          box-shadow: 0 2px 8px rgba(37, 99, 235, 0.25);
        }

        .filter-btn.active .badge-count {
          background: rgba(255, 255, 255, 0.25);
          color: #ffffff;
        }

        .search-wrap {
          position: relative;
          min-width: 320px;
          flex: 1;
          max-width: 440px;
        }

        .search-input {
          width: 100%;
          background: #ffffff;
          border: 1px solid #cbd5e1;
          border-radius: 10px;
          padding: 10px 36px 10px 14px;
          font-size: 13px;
          color: #0f172a;
          outline: none;
        }

        .search-input:focus {
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
        }

        .clear-search-btn {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          border: none;
          background: transparent;
          color: #94a3b8;
          cursor: pointer;
          font-size: 13px;
        }

        /* PRODUCT GRID */
        .product-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(330px, 1fr));
          gap: 20px;
        }

        .product-card {
          background: #ffffff;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 3px 14px rgba(0, 0, 0, 0.05);
          border: 1px solid #e2e8f0;
          display: flex;
          flex-direction: column;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .product-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
        }

        /* APPLICANT BANNER ON TOP OF CARD */
        .applicant-banner {
          background: #eff6ff;
          border-bottom: 1px solid #dbeafe;
          padding: 10px 14px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .store-badge-left {
          display: flex;
          align-items: center;
          gap: 8px;
          overflow: hidden;
        }

        .store-icon {
          font-size: 16px;
        }

        .store-meta {
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .store-title {
          font-size: 12.5px;
          color: #1e3a8a;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 150px;
        }

        .store-code-pill {
          font-size: 10px;
          font-weight: 700;
          color: #2563eb;
        }

        .history-pill-btn {
          background: #ffffff;
          color: #1d4ed8;
          border: 1px solid #bfdbfe;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 700;
          padding: 5px 9px;
          cursor: pointer;
          white-space: nowrap;
          transition: background 0.15s;
        }

        .history-pill-btn:hover {
          background: #dbeafe;
        }

        .applicant-subinfo {
          padding: 6px 14px;
          background: #f8fafc;
          border-bottom: 1px solid #f1f5f9;
          font-size: 11px;
          color: #64748b;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        /* PRODUCT IMAGE */
        .product-image {
          height: 180px;
          background: #fafafa;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .product-image img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          padding: 12px;
        }

        .no-image {
          font-size: 48px;
          color: #94a3b8;
        }

        .status-badge {
          position: absolute;
          top: 10px;
          right: 10px;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.3px;
        }

        .status-pending {
          background: #fef3c7;
          color: #92400e;
          border: 1px solid #fde68a;
        }

        .status-approved {
          background: #dcfce7;
          color: #166534;
          border: 1px solid #bbf7d0;
        }

        .status-suspended {
          background: #fef3c7;
          color: #92400e;
          border: 1px solid #fde68a;
        }

        .status-rejected {
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #fecaca;
        }

        /* PRODUCT INFO */
        .product-info {
          padding: 16px;
          display: flex;
          flex-direction: column;
          flex: 1;
        }

        .product-category-row {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 4px;
        }

        .product-category {
          color: #2563eb;
          font-size: 10.5px;
          font-weight: 800;
          text-transform: uppercase;
        }

        .product-brand-tag {
          background: #f1f5f9;
          color: #475569;
          padding: 2px 7px;
          border-radius: 4px;
          font-size: 10.5px;
          font-weight: 700;
        }

        .product-title {
          margin: 4px 0 6px 0;
          color: #0f172a;
          font-size: 16px;
          font-weight: 800;
        }

        .description {
          color: #64748b;
          font-size: 12.5px;
          line-height: 18px;
          min-height: 36px;
          margin: 0 0 12px 0;
        }

        /* VARIANTS */
        .variants {
          margin-top: auto;
          background: #f8fafc;
          border: 1px solid #f1f5f9;
          border-radius: 8px;
          padding: 8px;
          margin-bottom: 12px;
        }

        .variant-header-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 6px;
        }

        .variant-header-row strong {
          font-size: 11.5px;
          color: #334155;
        }

        .price-tag {
          font-size: 13px;
          font-weight: 800;
          color: #16a34a;
        }

        .variant-list {
          display: flex;
          gap: 6px;
          overflow-x: auto;
        }

        .variant {
          flex-shrink: 0;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          padding: 4px 6px;
          display: flex;
          gap: 6px;
          align-items: center;
          background: #ffffff;
        }

        .variant img, .variant-placeholder {
          width: 30px;
          height: 30px;
          object-fit: contain;
          border-radius: 4px;
          background: #f8fafc;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
        }

        .variant-info {
          display: flex;
          flex-direction: column;
        }

        .variant-color {
          font-size: 10px;
          color: #64748b;
        }

        .variant-price {
          font-size: 11px;
          color: #0f172a;
        }

        /* ACTION ROW */
        .action-row {
          display: flex;
          gap: 8px;
          margin-top: 6px;
        }

        .view-btn {
          background: #f1f5f9;
          color: #1e293b;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          padding: 9px 12px;
          cursor: pointer;
          font-weight: 700;
          font-size: 12.5px;
          flex: 1;
        }

        .view-btn:hover {
          background: #e2e8f0;
        }

        .approve-btn {
          background: #16a34a;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 9px 14px;
          cursor: pointer;
          font-weight: 700;
          font-size: 12.5px;
          flex: 1;
        }

        .approve-btn:hover {
          background: #15803d;
        }

        .reject-btn {
          background: #dc2626;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 9px 14px;
          cursor: pointer;
          font-weight: 700;
          font-size: 12.5px;
          flex: 1;
        }

        .reject-btn:hover {
          background: #b91c1c;
        }

        .history-view-btn {
          background: #eff6ff;
          color: #2563eb;
          border: 1px solid #bfdbfe;
          border-radius: 8px;
          padding: 9px 12px;
          cursor: pointer;
          font-weight: 700;
          font-size: 12px;
          flex: 1;
        }

        .suspend-btn {
          background: #fef3c7;
          color: #92400e;
          border: 1px solid #fde68a;
          border-radius: 8px;
          padding: 9px 12px;
          cursor: pointer;
          font-weight: 700;
          font-size: 12px;
          transition: all 0.15s ease;
          white-space: nowrap;
        }

        .suspend-btn:hover {
          background: #fde68a;
        }

        .unsuspend-btn {
          background: #dcfce7;
          color: #15803d;
          border: 1px solid #bbf7d0;
          border-radius: 8px;
          padding: 9px 12px;
          cursor: pointer;
          font-weight: 700;
          font-size: 12px;
          transition: all 0.15s ease;
          white-space: nowrap;
        }

        .unsuspend-btn:hover {
          background: #bbf7d0;
        }

        .delete-btn {
          background: #fee2e2;
          color: #dc2626;
          border: 1px solid #fecaca;
          border-radius: 8px;
          padding: 9px 12px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.15s ease;
        }

        .delete-btn:hover {
          background: #fca5a5;
        }

        .modal-delete-btn {
          background: #fee2e2;
          color: #dc2626;
          border: 1px solid #fecaca;
          border-radius: 10px;
          padding: 10px 18px;
          cursor: pointer;
          font-weight: 700;
          font-size: 13px;
          transition: all 0.15s ease;
          margin-right: auto;
        }

        .modal-delete-btn:hover {
          background: #fca5a5;
        }

        .modal-suspend-btn {
          background: #fef3c7;
          color: #92400e;
          border: 1px solid #fde68a;
          border-radius: 10px;
          padding: 10px 18px;
          cursor: pointer;
          font-weight: 700;
          font-size: 13px;
          transition: all 0.15s ease;
        }

        .modal-suspend-btn:hover {
          background: #fde68a;
        }

        .modal-unsuspend-btn {
          background: #dcfce7;
          color: #15803d;
          border: 1px solid #bbf7d0;
          border-radius: 10px;
          padding: 10px 18px;
          cursor: pointer;
          font-weight: 700;
          font-size: 13px;
          transition: all 0.15s ease;
        }

        .modal-unsuspend-btn:hover {
          background: #bbf7d0;
        }

        .mini-delete {
          background: #fee2e2;
          color: #dc2626;
          border: 1px solid #fecaca;
          padding: 5px 8px;
          border-radius: 6px;
          font-size: 11px;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .mini-delete:hover {
          background: #fca5a5;
        }


        /* EMPTY & LOADING */
        .loading-box, .empty-box {
          background: white;
          border-radius: 16px;
          padding: 60px 20px;
          text-align: center;
          color: #64748b;
          border: 1px solid #e2e8f0;
        }

        .empty-icon {
          font-size: 46px;
          margin-bottom: 12px;
        }

        .spinner {
          width: 36px;
          height: 36px;
          border: 4px solid #dbeafe;
          border-top-color: #2563eb;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: auto;
          margin-bottom: 12px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* MODAL OVERLAYS */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.7);
          backdrop-filter: blur(3px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          z-index: 9999;
        }

        .product-modal, .store-history-modal {
          width: 100%;
          max-width: 680px;
          max-height: 90vh;
          overflow-y: auto;
          background: white;
          border-radius: 18px;
          box-shadow: 0 24px 60px rgba(0, 0, 0, 0.25);
          display: flex;
          flex-direction: column;
        }

        .store-history-modal {
          max-width: 820px;
        }

        .modal-header {
          padding: 18px 24px;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #ffffff;
          position: sticky;
          top: 0;
          z-index: 10;
        }

        .modal-header h2 {
          margin: 0;
          color: #0f172a;
          font-size: 19px;
          font-weight: 800;
        }

        .modal-header p {
          margin: 4px 0 0;
          color: #64748b;
          font-size: 12.5px;
        }

        .close-btn {
          width: 36px;
          height: 36px;
          border: none;
          border-radius: 50%;
          background: #f1f5f9;
          font-size: 24px;
          cursor: pointer;
          color: #475569;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .close-btn:hover {
          background: #e2e8f0;
        }

        .modal-content, .history-modal-content {
          padding: 22px;
          flex: 1;
        }

        /* STORE APPLICANT SUMMARY CARD INSIDE MODAL */
        .store-applicant-card {
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          border-radius: 14px;
          padding: 16px;
          margin-bottom: 20px;
        }

        .store-card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 12px;
        }

        .applicant-badge {
          font-size: 10px;
          font-weight: 800;
          color: #166534;
          letter-spacing: 0.5px;
        }

        .store-card-header h3 {
          margin: 3px 0 0 0;
          color: #14532d;
          font-size: 16px;
        }

        .code-pill {
          background: #dcfce7;
          color: #15803d;
          border: 1px solid #86efac;
          padding: 3px 8px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 800;
        }

        .applicant-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-bottom: 12px;
        }

        .applicant-field {
          display: flex;
          flex-direction: column;
        }

        .applicant-field span {
          font-size: 11px;
          color: #4b5563;
        }

        .applicant-field strong {
          font-size: 12.5px;
          color: #111827;
        }

        .store-history-cta {
          width: 100%;
          background: #16a34a;
          color: white;
          border: none;
          padding: 9px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
        }

        .store-history-cta:hover {
          background: #15803d;
        }

        .detail-row {
          display: flex;
          justify-content: space-between;
          gap: 20px;
          padding: 10px 0;
          border-bottom: 1px solid #f1f5f9;
          font-size: 13.5px;
        }

        .detail-row span {
          color: #64748b;
        }

        .detail-row strong {
          color: #0f172a;
        }

        .description-box {
          background: #f8fafc;
          padding: 14px;
          border-radius: 10px;
          margin: 16px 0;
        }

        .description-box h4 {
          margin: 0 0 6px 0;
          font-size: 13px;
          color: #334155;
        }

        .description-box p {
          margin: 0;
          color: #64748b;
          font-size: 13px;
          line-height: 20px;
        }

        .rejection-reason-box {
          background: #fee2e2;
          border: 1px solid #fecaca;
          border-radius: 10px;
          padding: 12px;
          margin: 14px 0;
          color: #991b1b;
          font-size: 13px;
        }

        .modal-variants {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
          gap: 10px;
          margin-top: 10px;
        }

        .modal-variant {
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          padding: 10px;
          display: flex;
          gap: 10px;
          background: #f8fafc;
        }

        .modal-variant img, .modal-variant-placeholder {
          width: 60px;
          height: 60px;
          object-fit: contain;
          background: #ffffff;
          border-radius: 6px;
          border: 1px solid #e2e8f0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
        }

        .modal-variant strong {
          font-size: 12px;
          color: #0f172a;
        }

        .modal-variant p {
          margin: 3px 0 0 0;
          color: #64748b;
          font-size: 11.5px;
        }

        .modal-actions {
          display: flex;
          gap: 12px;
          padding: 16px 24px;
          border-top: 1px solid #e2e8f0;
          background: #ffffff;
        }

        .modal-actions button {
          flex: 1;
        }

        .close-modal-btn {
          background: #f1f5f9;
          color: #334155;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          padding: 10px 16px;
          font-weight: 700;
          font-size: 13px;
          cursor: pointer;
        }

        /* HISTORY MODAL SPECIFICS */
        .history-stats-bar {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
          margin-bottom: 18px;
        }

        .stat-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 12px;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .stat-label {
          font-size: 11px;
          color: #64748b;
          font-weight: 700;
          margin-bottom: 2px;
        }

        .stat-value {
          font-size: 18px;
          font-weight: 900;
          color: #0f172a;
        }

        .approved-stat { border-color: #bbf7d0; background: #f0fdf4; }
        .approved-stat .stat-value { color: #166534; }
        .pending-stat { border-color: #fde68a; background: #fffbeb; }
        .pending-stat .stat-value { color: #92400e; }
        .rejected-stat { border-color: #fecaca; background: #fef2f2; }
        .rejected-stat .stat-value { color: #991b1b; }

        .history-filter-row {
          display: flex;
          gap: 8px;
          margin-bottom: 16px;
        }

        .sub-filter {
          background: #ffffff;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          padding: 6px 12px;
          font-size: 12px;
          font-weight: 700;
          color: #475569;
          cursor: pointer;
        }

        .sub-filter.active {
          background: #2563eb;
          color: #ffffff;
          border-color: #2563eb;
        }

        .history-product-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 14px;
        }

        .history-product-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .history-card-img {
          height: 120px;
          background: #f8fafc;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .history-card-img img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          padding: 8px;
        }

        .history-card-body {
          padding: 10px;
          display: flex;
          flex-direction: column;
          flex: 1;
        }

        .history-category {
          font-size: 10px;
          font-weight: 800;
          color: #2563eb;
          text-transform: uppercase;
        }

        .history-card-body h4 {
          margin: 3px 0 4px 0;
          font-size: 13px;
          color: #0f172a;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .history-brand, .history-price {
          font-size: 11.5px;
          color: #64748b;
          margin: 2px 0;
        }

        .history-date {
          font-size: 10.5px;
          color: #94a3b8;
          margin-top: auto;
          margin-bottom: 6px;
        }

        .history-action-btns {
          display: flex;
          gap: 6px;
          margin-top: 6px;
        }

        .mini-approve {
          flex: 1;
          background: #16a34a;
          color: white;
          border: none;
          padding: 5px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 700;
          cursor: pointer;
        }

        .mini-reject {
          flex: 1;
          background: #dc2626;
          color: white;
          border: none;
          padding: 5px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 700;
          cursor: pointer;
        }

        @media (max-width: 680px) {
          .approval-page {
            padding: 12px;
          }

          .page-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .controls-bar {
            flex-direction: column;
            align-items: stretch;
          }

          .search-wrap {
            max-width: 100%;
          }

          .product-grid {
            grid-template-columns: 1fr;
          }

          .applicant-grid {
            grid-template-columns: 1fr;
          }

          .history-stats-bar {
            grid-template-columns: 1fr 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default ProductApproval;